import React, {useState, KeyboardEvent, ChangeEvent} from "react";
import {useNavigate, Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {ROUTES} from "../constants/routes";

interface MenuSection {
  title: string;
  items: {label: string; link: string}[];
}

interface BurgerMenuProps {
  isOpen: boolean;
}

export function BurgerMenu({isOpen}: BurgerMenuProps) {
  const {t} = useTranslation();
  const [query, setQuery] = useState<string>("");
  const [openSection, setOpenSection] = useState<number | null>(null);
  const navigate = useNavigate();

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && query.trim()) handleSearch();
  };

  const handleSearch = () => {
    if (query.trim()) navigate(ROUTES.search(query.trim()));
  };

  if (!isOpen) return null;

  const menuItems: MenuSection[] = [
    {
      title: t("movies"),
      items: [
        {label: t("popular_movies"), link: ROUTES.discover("movie", "popular")},
        {label: t("best_movies"), link: ROUTES.discover("movie", "best")},
        {label: t("upcoming_movies"), link: ROUTES.discover("movie", "upcoming")},
      ],
    },
    {
      title: t("shows"),
      items: [
        {label: t("popular_shows"), link: ROUTES.discover("tv", "popular")},
        {label: t("best_shows"), link: ROUTES.discover("tv", "best")},
        {label: t("on_the_air"), link: ROUTES.discover("tv", "upcoming")},
      ],
    },
  ];

  return (
    <nav className="w-full h-full bg-bg">
      <div className="flex flex-col gap-4">
        {menuItems.map((menu, index) => (
          <div key={index} className="w-full">
            <button
              onClick={() => setOpenSection(openSection === index ? null : index)}
              className="w-full flex items-center justify-between px-4 py-3 text-t1 font-medium hover:text-accent transition-colors duration-fast"
            >
              <span>{menu.title}</span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`transition-transform duration-fast ${openSection === index ? "rotate-180" : ""}`}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {openSection === index && (
              <div className="flex flex-col bg-raised rounded-md border border-border mx-2 mb-1 overflow-hidden">
                {menu.items.map((item, idx) => (
                  <Link
                    key={idx}
                    to={item.link}
                    className="px-4 py-2.5 text-sm text-t2 hover:bg-accent hover:text-t1 transition-colors duration-fast"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}

        <div className="px-4 pt-2">
          <div className="relative">
            <input
              type="text"
              placeholder={t("search")}
              className="w-full border-b border-border2 outline-none text-base py-2 pl-2.5 pr-10 text-t1 bg-transparent transition-colors duration-fast placeholder:text-t3 focus:border-accent"
              value={query}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              onKeyDown={handleKeyPress}
            />
            <button
              className="absolute right-0 bottom-2 text-t3 hover:text-accent transition-colors duration-fast"
              onClick={handleSearch}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16">
                <path
                  fill="currentColor"
                  d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
